/**
 * Scroll the checkout's own scroll container back to the top.
 *
 * The checkout renders both as a full-page surface (standalone widget, light DOM)
 * and inside the shadow DOM of an embedded webcomponent overlay. A plain
 * `document.querySelector("[data-checkout-content]")` cannot pierce the shadow
 * boundary, so in the embedded case it returns null. Callers used to fall back to
 * `window.scrollTo(...)` — which, inside a full-screen modal, scrolls the *host
 * page* (e.g. the school's Wix site) to the top. That is never desired: a modal
 * must not move the page behind it.
 *
 * This helper resolves the scroll container across both contexts and, crucially,
 * NEVER touches `window`. If no container is found it no-ops.
 */
export function scrollCheckoutContentToTop(): void {
  const container = findCheckoutScrollContainer();
  container?.scrollTo({ top: 0, behavior: "smooth" });
}

function findCheckoutScrollContainer(): HTMLElement | null {
  const selector =
    '[data-checkout-content], [class*="overflow-y-auto"], [class*="overflow-auto"]';

  // Light DOM (standalone widget / full-page surface).
  const inDocument = document.querySelector(selector) as HTMLElement | null;
  if (inDocument) return inDocument;

  // Embedded webcomponent: the checkout lives inside an open shadow root. The
  // overlay host is a direct child of <body>, so a single-level scan of
  // top-level shadow roots reaches it without coupling to specific tag names.
  for (const el of Array.from(document.body.querySelectorAll("*"))) {
    const shadowRoot = (el as Element & { shadowRoot?: ShadowRoot | null })
      .shadowRoot;
    if (!shadowRoot) continue;
    const inShadow = shadowRoot.querySelector(selector) as HTMLElement | null;
    if (inShadow) return inShadow;
  }

  return null;
}
